import type {
  CreateDeploymentPayload,
  CreateDsAppPayload,
  Deployment,
  DeploymentDetails,
  DeploymentLogItem,
  DeploymentStats,
  DeploymentTemplate,
  DsApp,
  PaginatedList,
  Parameter,
  ParameterTemplate,
  ThirdPartyContract,
} from "../types/pdm.types";

const DS_APP_SEED_ID = "8a35d97e-ce3e-409f-9d79-742c11a9488a";

const seedDsApps: DsApp[] = [
  {
    id: DS_APP_SEED_ID,
    name: "Securitize Bridge",
    version: "0.0.1",
    description: "The Securitize Bridge ds-App...",
    repositoryUrl: "git@github.com:securitize-io/bc-securitize-bridge-sc.git",
    gitBranch: "master",
    createdAt: "2026-03-12T00:30:16.641Z",
    updateAt: "2026-03-18T01:21:39.499Z",
  },
];

let mockDsAppsStore: DsApp[] = [...seedDsApps];

export const mockTemplates: DeploymentTemplate[] = [
  {
    id: "2f8a8578-faf8-4778-b1a3-030e68529c00",
    name: "Bridge USDC",
    contractName: "USDCBridgeV2",
    description: "Cross-chain USDC transfers with configurable gas limits",
    deploymentTask: "npx hardhat deploy-usdc-bridge-protocol",
    postDeploymentInstructions: [
      {
        order: 1,
        title: "Verify contracts",
        description:
          "Confirm the deployed addresses match expectations and are verified on the relevant block explorer.",
        link: "https://example.com/verify",
      },
      {
        order: 2,
        title: "Update monitoring",
        description:
          "Ensure alerting and dashboards are configured for the new deployment.",
      },
    ],
    initializationSchema: {
      type: "object",
      title: "DeployUSDCBridgeProtocolParams",
      $schema: "http://json-schema.org/draft-07/schema#",
      required: [
        "network",
        "wormhole-core",
        "quoter-router",
        "quoter-address",
        "token-messenger",
        "usdc",
      ],
      properties: {
        usdc: {
          type: "string",
          pattern: "^0x[a-fA-F0-9]{40}$",
          description: "USDC contract address on the target network",
        },
        network: {
          type: "string",
          examples: ["sepolia", "ethereum", "polygon", "arbitrum"],
          description:
            "Target Hardhat network where the USDC bridge ds-App will be deployed",
        },
        "quoter-router": {
          type: "string",
          pattern: "^0x[a-fA-F0-9]{40}$",
          description: "Address of the router used for swap quoting",
        },
        "wormhole-core": {
          type: "string",
          pattern: "^0x[a-fA-F0-9]{40}$",
          description: "Address of the Wormhole Core contract",
        },
        "quoter-address": {
          type: "string",
          pattern: "^0x[a-fA-F0-9]{40}$",
          description: "Address of the Quoter contract",
        },
        "token-messenger": {
          type: "string",
          pattern: "^0x[a-fA-F0-9]{40}$",
          description: "Circle CCTP TokenMessenger contract address",
        },
      },
      additionalProperties: false,
    },
    protocolId: DS_APP_SEED_ID,
    createdAt: "2026-03-12T02:17:45.696Z",
    updatedAt: "2026-03-26T03:02:29.794Z",
  },
];

export const mockThirdPartyContracts: ThirdPartyContract[] = [
  {
    id: "f3ee4c1a-b150-4efa-b024-acd3454b9801",
    network: "ethereum-sepolia",
    networkType: "testnet",
    protocolName: "Quoter Address ",
    contractName: null,
    address: "0x5241c9276698439fef2780dbab76fec90b633fbd",
    version: "1",
    description: "Wormhole Labs’ Testnet Quoter public key",
    createdAt: "2026-03-26T03:08:56.464Z",
    updatedAt: "2026-03-26T03:12:44.018Z",
  },
];

let mockDeploymentsStore: Deployment[] = [
  {
    id: "dep-1",
    name: "Bridge to Avalanche",
    status: "success",
    network: "avalanche",
    deploymentTemplateId: mockTemplates[0].id,
    deploymentTemplate: {
      id: mockTemplates[0].id,
      name: mockTemplates[0].name,
    },
    description: "Test deployment",
    createdAt: "2026-03-20T10:00:00.000Z",
  },
  {
    id: "dep-2",
    name: "Pending rollout",
    status: "pending",
    network: "ethereum",
    deploymentTemplateId: mockTemplates[0].id,
    deploymentTemplate: {
      id: mockTemplates[0].id,
      name: mockTemplates[0].name,
    },
    createdAt: "2026-03-25T12:00:00.000Z",
  },
  {
    id: "dep-3",
    name: "Active job",
    status: "in_progress",
    network: "optimism",
    deploymentTemplateId: mockTemplates[0].id,
    deploymentTemplate: {
      id: mockTemplates[0].id,
      name: mockTemplates[0].name,
    },
    createdAt: "2026-03-26T09:00:00.000Z",
  },
  {
    id: "dep-4",
    name: "Failed deployment",
    status: "failed",
    network: "arbitrum",
    deploymentTemplateId: mockTemplates[0].id,
    deploymentTemplate: {
      id: mockTemplates[0].id,
      name: mockTemplates[0].name,
    },
    description: "Simulated failure for retry testing",
    createdAt: "2026-03-26T10:15:00.000Z",
  },
];

const mockLogItemsByDeployment: Record<string, DeploymentLogItem[]> = {
  "dep-1": [
    {
      id: "dep-1-log-1",
      step: 1,
      level: "info",
      message: "Starting deployment pipeline",
      timestamp: "2026-03-20T10:00:01.000Z",
    },
    {
      id: "dep-1-log-2",
      step: 1,
      level: "info",
      message: "Resolved template Bridge USDC",
      timestamp: "2026-03-20T10:00:05.000Z",
    },
    {
      id: "dep-1-log-3",
      step: 2,
      level: "info",
      message: "Contract deployed at 0xabc...",
      timestamp: "2026-03-20T10:00:12.000Z",
    },
    {
      id: "dep-1-log-4",
      step: 3,
      level: "info",
      message: "Deployment finished successfully",
      timestamp: "2026-03-20T10:00:15.000Z",
    },
  ],
  "dep-2": [
    {
      id: "dep-2-log-1",
      step: 1,
      level: "warn",
      message: "Queued for deployment",
      timestamp: "2026-03-25T12:00:00.000Z",
    },
  ],
  "dep-3": [
    {
      id: "dep-3-log-1",
      step: 1,
      level: "info",
      message: "Provisioning runner",
      timestamp: "2026-03-26T09:00:10.000Z",
    },
    {
      id: "dep-3-log-2",
      step: 2,
      level: "info",
      message: "Running hardhat task",
      timestamp: "2026-03-26T09:00:22.000Z",
    },
    {
      id: "dep-3-log-3",
      step: 2,
      level: "warn",
      message: "Waiting for confirmations…",
      timestamp: "2026-03-26T09:00:45.000Z",
    },
    {
      id: "dep-3-log-4",
      step: 3,
      level: "error",
      message: "RPC timeout while awaiting receipt",
      timestamp: "2026-03-26T09:01:08.000Z",
    },
  ],
};

function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export async function mockGetDsApps(
  offset: number,
  limit: number,
  nameFilter: string,
): Promise<PaginatedList<DsApp>> {
  await sleep(150);
  let items = [...mockDsAppsStore];
  const q = nameFilter.trim().toLowerCase();
  if (q) {
    items = items.filter((p) => p.name.toLowerCase().includes(q));
  }
  const total = items.length;
  items = items.slice(offset, offset + limit);
  return { items, total };
}

export async function mockGetDsApp(id: string): Promise<DsApp> {
  await sleep(120);
  const dsApp = mockDsAppsStore.find((p) => p.id === id);
  if (!dsApp) {
    throw new Error("DSApp not found");
  }
  return { ...dsApp };
}

export async function mockCreateDsApp(
  payload: CreateDsAppPayload,
): Promise<DsApp> {
  await sleep(200);
  const now = new Date().toISOString();
  const dsApp: DsApp = {
    id: `ds-app-${Math.random().toString(36).slice(2, 12)}`,
    name: payload.name,
    version: payload.version,
    description: payload.description,
    repositoryUrl: payload.repositoryUrl,
    gitBranch: payload.gitBranch,
    createdAt: now,
    updateAt: now,
  };
  mockDsAppsStore = [dsApp, ...mockDsAppsStore];
  return dsApp;
}

export async function mockGetTemplates(
  protocolId: string,
  offset: number,
  limit: number,
): Promise<PaginatedList<DeploymentTemplate>> {
  await sleep(150);
  let items = mockTemplates.filter((t) => t.protocolId === protocolId);
  const total = items.length;
  items = items.slice(offset, offset + limit);
  return { items, total };
}

export async function mockGetDeploymentTemplates(
  offset: number,
  limit: number,
): Promise<PaginatedList<DeploymentTemplate>> {
  await sleep(150);
  const total = mockTemplates.length;
  const items = mockTemplates.slice(offset, offset + limit).map((t) => ({ ...t }));
  return { items, total };
}

export async function mockGetDeploymentTemplate(
  id: string,
): Promise<DeploymentTemplate> {
  await sleep(150);
  const template = mockTemplates.find((t) => t.id === id);
  if (!template) {
    throw new Error("Deployment template not found");
  }
  return { ...template };
}

/** Preset keys align with `mockTemplates[0].initializationSchema` (excluding `network`). */
const mockParameterPresetAddresses: Record<string, string> = {
  "wormhole-core": "0x1111111111111111111111111111111111111111",
  "quoter-router": "0x2222222222222222222222222222222222222222",
  "quoter-address": "0x3333333333333333333333333333333333333333",
  "token-messenger": "0x4444444444444444444444444444444444444444",
  usdc: "0x5555555555555555555555555555555555555555",
};

export async function mockGetParameterTemplates(
  deploymentTemplateId: string,
  network: string,
  offset: number,
  limit: number,
): Promise<PaginatedList<ParameterTemplate>> {
  await sleep(150);
  const template = mockTemplates.find((t) => t.id === deploymentTemplateId);
  if (!template) {
    return { items: [], total: 0 };
  }
  const presetParameters: Record<string, Parameter> = {};
  Object.entries(mockParameterPresetAddresses).forEach(([key, address]) => {
    presetParameters[key] = {
      thirdPartyContractId: `tp-${key}`,
      address,
      description: `Preset for ${key}`,
    };
  });
  // First row is used by the UI for copy and presets (see CreateDeployment).
  const item: ParameterTemplate = {
    id: "pt-mock-1",
    network,
    name: `${template.name} parameter presets`,
    description:
      "Preset third-party contract addresses for this deployment template and network. Review mandatory values before submitting.",
    presetParameters,
    requiredParameters: null,
    deploymentTemplateId,
    createdAt: "2026-03-12T02:17:45.696Z",
    updatedAt: "2026-03-26T03:02:29.794Z",
  };
  const all = [item];
  const items = all.slice(offset, offset + limit);
  return { items, total: all.length };
}

export async function mockGetThirdPartyContracts(
  network?: string,
  networkType?: string,
): Promise<ThirdPartyContract[]> {
  await sleep(150);
  return mockThirdPartyContracts.filter((c) => {
    if (network && c.network !== network) {
      return false;
    }
    if (networkType && c.networkType !== networkType) {
      return false;
    }
    return true;
  });
}

export async function mockGetThirdPartyContract(
  id: string,
): Promise<ThirdPartyContract> {
  await sleep(120);
  const found = mockThirdPartyContracts.find((c) => c.id === id);
  if (!found) {
    throw new Error(`Third party contract not found: ${id}`);
  }
  return found;
}

export async function mockGetDeployments(
  offset: number,
  limit: number,
  filters?: { status?: string; network?: string; deploymentTemplateId?: string },
): Promise<PaginatedList<Deployment>> {
  await sleep(120);
  const filtered = mockDeploymentsStore.filter((d) => {
    if (filters?.status && d.status !== filters.status) return false;
    if (filters?.network && d.network !== filters.network) return false;
    if (
      filters?.deploymentTemplateId &&
      d.deploymentTemplateId !== filters.deploymentTemplateId
    ) {
      return false;
    }
    return true;
  });
  const items = filtered.slice(offset, offset + limit);
  return { items, total: filtered.length };
}

export async function mockGetDeployment(id: string): Promise<DeploymentDetails> {
  await sleep(150);
  const deployment = mockDeploymentsStore.find((d) => d.id === id);
  if (!deployment) {
    throw new Error("Deployment not found");
  }

  return {
    id: deployment.id,
    name: deployment.name,
    requiredParameters: {
      token: "0x7BBb0e4f7A00350ab6cAD82aaAA9827100094C45",
    },
    optionalParameters: {
      "optional-contract": "0x7BBb0e4f7A00350ab6cAD82aaAA9827100094C45",
    },
    network: deployment.network,
    provider: deployment.network,
    status: deployment.status,
    deploymentTemplateId: deployment.deploymentTemplateId,
    description: deployment.description ?? "",
    commitHash: "f4c3b00c5eec0de1234567890abcdef123456789",
    commitMessage: "Release ds-App v1.0.0",
    commitAuthor: "alice@example.com",
    commitDate: "2024-01-01T12:00:00.000Z",
    ownerAddress: "0x1234567890abcdef1234567890abcdef12345678",
    deployedBy: "0xabcdefabcdefabcdefabcdefabcdefabcdefabcd",
    branch: "feat/example-branch",
    address: "0xF20c86B6c666E0aAEcF5eB2994ddcacc5E6ED4C8",
    errorMessage: deployment.status === "failed" ? "Mock deployment failed" : null,
    startedAt: "2024-01-01T12:00:00.000Z",
    completedAt: "2024-01-01T12:05:00.000Z",
    createdAt: deployment.createdAt,
    updateAt: "2024-01-01T12:06:00.000Z",
    updatedAt: deployment.updatedAt ?? null,
  };
}

export async function mockGetDeploymentStats(): Promise<DeploymentStats> {
  await sleep(80);
  const success = mockDeploymentsStore.filter(
    (d) => d.status === "success",
  ).length;
  const pending = mockDeploymentsStore.filter(
    (d) => d.status === "pending",
  ).length;
  const inProgress = mockDeploymentsStore.filter(
    (d) => d.status === "in_progress",
  ).length;
  return {
    totalSuccess: success,
    totalPending: pending,
    inProgress,
  };
}

export async function mockGetDeploymentLogsPage(
  deploymentId: string,
  offset: number,
  limit: number,
): Promise<PaginatedList<DeploymentLogItem>> {
  await sleep(120);
  const all = mockLogItemsByDeployment[deploymentId] ?? [];
  const total = all.length;
  const items = all.slice(offset, offset + limit);
  return { items, total };
}

export async function mockRetryDeployment(deploymentId: string): Promise<void> {
  await sleep(350);
  const idx = mockDeploymentsStore.findIndex((d) => d.id === deploymentId);
  if (idx === -1) {
    throw new Error("Deployment not found");
  }
  const deployment = mockDeploymentsStore[idx];
  if (deployment.status !== "failed") {
    throw new Error("Only failed deployments can be retried");
  }
  mockDeploymentsStore = [
    ...mockDeploymentsStore.slice(0, idx),
    { ...deployment, status: "pending", updatedAt: new Date().toISOString() },
    ...mockDeploymentsStore.slice(idx + 1),
  ];
}

export async function mockCreateDeployment(
  payload: CreateDeploymentPayload,
): Promise<Deployment> {
  await sleep(200);
  const deployment: Deployment = {
    id: `dep-${Math.random().toString(36).slice(2, 10)}`,
    name: payload.name,
    status: "pending",
    network: payload.network,
    deploymentTemplateId: payload.deploymentTemplateId,
    description: payload.description,
    createdAt: new Date().toISOString(),
  };
  mockDeploymentsStore = [deployment, ...mockDeploymentsStore];
  const now = new Date();
  mockLogItemsByDeployment[deployment.id] = [
    {
      id: `${deployment.id}-log-1`,
      step: 1,
      level: "info",
      message: "Deployment created",
      timestamp: now.toISOString(),
    },
    {
      id: `${deployment.id}-log-2`,
      step: 1,
      level: "info",
      message: `Template ${payload.deploymentTemplateId}`,
      timestamp: new Date(now.getTime() + 1000).toISOString(),
    },
  ];
  return deployment;
}

export function mockGetCommitmentLevels(): Promise<
  { value: number; level: string }[]
> {
  return Promise.resolve([
    { value: 200, level: "instant" },
    { value: 201, level: "safe" },
    { value: 202, level: "finalized" },
    { value: 203, level: "custom" },
  ]);
}
